import React, { useEffect, useState } from "react";
import { ErrorBox, Stat } from "./common";
import { errorMessage, fmtMoney, fmtNum, toISODate } from "../dmApi";

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);
  return { date_from: toISODate(from), date_to: toISODate(to) };
}

/** Spend, leads, cost per lead and per-platform breakdown for a date range. */
function ProjectSummary({ api, projectId, refreshKey, compact = false }) {
  const [range, setRange] = useState(defaultRange);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .summary(projectId, range)
      .then((data) => {
        if (cancelled) return;
        setSummary(data);
        setError("");
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, "Could not load summary"));
      });
    return () => {
      cancelled = true;
    };
  }, [api, projectId, range, refreshKey]);

  const t = summary?.in_range || {};
  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">
        Results
        <span className="dmp-actions">
          <input
            type="date"
            value={range.date_from}
            onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
          />
          <span className="dmp-muted">to</span>
          <input type="date" value={range.date_to} onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))} />
        </span>
      </h3>
      <ErrorBox message={error} />
      <div className="dmp-stats">
        <Stat label="Impressions" value={fmtNum(t.Impressions)} />
        <Stat label="Reach" value={fmtNum(t.Reach)} />
        <Stat label="Views" value={fmtNum(t.Views)} />
        <Stat label="Clicks" value={fmtNum(t.Clicks)} />
        <Stat label="CTR" value={t.CTR != null ? `${t.CTR}%` : "—"} />
        <Stat label="Leads (reported)" value={fmtNum(t.Leads)} />
        <Stat label="Leads in CRM" value={fmtNum(summary?.crm_leads)} />
        <Stat label="Spend" value={fmtMoney(t.Spend)} />
        <Stat label="Cost per lead" value={t.Cost_Per_Lead != null ? fmtMoney(t.Cost_Per_Lead) : "—"} />
        <Stat label="Active budget / day" value={fmtMoney(summary?.campaigns?.active_budget_per_day)} />
      </div>

      {!compact && (summary?.by_platform || []).length > 0 ? (
        <div className="dmp-table-wrap">
          <table className="dmp-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th className="dmp-num">Impressions</th>
                <th className="dmp-num">Reach</th>
                <th className="dmp-num">Views</th>
                <th className="dmp-num">Clicks</th>
                <th className="dmp-num">Likes</th>
                <th className="dmp-num">Shares</th>
                <th className="dmp-num">Leads</th>
                <th className="dmp-num">Spend</th>
                <th className="dmp-num">CPL</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_platform.map((row) => (
                <tr key={row.Platform}>
                  <td>{row.Platform}</td>
                  <td className="dmp-num">{fmtNum(row.Impressions)}</td>
                  <td className="dmp-num">{fmtNum(row.Reach)}</td>
                  <td className="dmp-num">{fmtNum(row.Views)}</td>
                  <td className="dmp-num">{fmtNum(row.Clicks)}</td>
                  <td className="dmp-num">{fmtNum(row.Likes)}</td>
                  <td className="dmp-num">{fmtNum(row.Shares)}</td>
                  <td className="dmp-num">{fmtNum(row.Leads)}</td>
                  <td className="dmp-num">{fmtMoney(row.Spend)}</td>
                  <td className="dmp-num">{row.Cost_Per_Lead != null ? fmtMoney(row.Cost_Per_Lead) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {compact && summary ? (
        <div className="dmp-chips">
          {Object.entries(summary.content || {}).map(([status, count]) => (
            <span key={status} className="dmp-muted">
              {status}: <strong>{count}</strong>
            </span>
          ))}
          <span className="dmp-muted">
            Campaigns: <strong>{summary.campaigns?.active}</strong> active / {summary.campaigns?.total}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default ProjectSummary;
